/**
 * Utilities for serializing and deserializing search state to/from URL params
 */

import { INTERNAL_ROUTING } from 'shared/constants';
/**
 * Serialize search term and filters to URL query string
 * @param {string} term - Search term
 * @param {Object} filters - Filters object with Set values
 * @returns {string} URL query string (e.g., "term=heart&specialty=CARDIOLOGY,NEUROLOGY")
 */
export const serializeFiltersToUrl = (term, filters) => {
  const params = new URLSearchParams();

  if (term) {
    params.set(INTERNAL_ROUTING.QUERY_PARAMS.SEARCH_TERM, term);
  }

  // Safety check for filters
  if (!filters || typeof filters !== 'object') {
    return params.toString();
  }

  // Mapping from internal filter keys to URL-friendly param names
  const filterMapping = {
    SPECIALTY: 'specialty',
    SUBSPECIALTY: 'subspecialty',
    CONTENT_TYPE: 'contentType',
    TARGET_AUDIENCE: 'targetAudience',
    LABELS: 'labels',
  };

  Object.entries(filters).reduce((acc, [key, valueSet]) => {
    if (valueSet && typeof valueSet.size === 'number' && valueSet.size > 0) {
      const urlParam = filterMapping[key];
      if (urlParam) {
        const values = Array.from(valueSet).sort().join(',');
        acc.set(urlParam, values);
      }
    }
    return acc;
  }, params);

  return params.toString();
};

/**
 * Deserialize URL query params back to filters object
 * @param {URLSearchParams} searchParams - URLSearchParams object
 * @returns {Object} Filters object with Set values
 */
export const deserializeFiltersFromUrl = searchParams => {
  // Mapping from URL param names to internal filter keys
  const urlToFilterMapping = {
    specialty: 'SPECIALTY',
    subspecialty: 'SUBSPECIALTY',
    contentType: 'CONTENT_TYPE',
    targetAudience: 'TARGET_AUDIENCE',
    labels: 'LABELS',
  };

  return Object.entries(urlToFilterMapping).reduce((filters, [urlParam, filterKey]) => {
    const value = searchParams.get(urlParam);
    filters[filterKey] = value ? new Set(value.split(',').filter(v => v.trim())) : new Set();
    return filters;
  }, {});
};
